import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Code2, Zap, Globe2, Lightbulb, ArrowRight, LayoutDashboard, Users, BarChart3, AppWindow, Workflow, Link2, FileBarChart, Settings, CalendarCheck, BookOpen, UserCircle, Globe, Compass, Boxes, Sparkles } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  })
};

const serviceBlocks = [
  {
    icon: Code2,
    title: 'Digital Systems Development',
    desc: 'Custom platforms designed to run your business efficiently.',
    features: [
      { icon: LayoutDashboard, label: 'Admin dashboards' },
      { icon: Users, label: 'Client and user portals' },
      { icon: BarChart3, label: 'Data and reporting systems' },
      { icon: AppWindow, label: 'Custom web applications' },
    ]
  },
  {
    icon: Zap,
    title: 'Business Process Automation',
    desc: 'Reduce manual work and improve accuracy.',
    features: [
      { icon: Workflow, label: 'Workflow automation' },
      { icon: Link2, label: 'CRM integrations' },
      { icon: FileBarChart, label: 'Reporting automation' },
      { icon: Settings, label: 'Operational efficiency tools' },
    ]
  },
  {
    icon: Globe2,
    title: 'Web Platforms & Applications',
    desc: 'Modern, responsive, and scalable platforms.',
    features: [
      { icon: CalendarCheck, label: 'Booking systems' },
      { icon: BookOpen, label: 'Learning platforms' },
      { icon: UserCircle, label: 'Client portals' },
      { icon: Globe, label: 'Business websites' },
    ]
  },
  {
    icon: Lightbulb,
    title: 'Consulting & Strategy',
    desc: 'Make the right decisions before you build.',
    features: [
      { icon: Compass, label: 'System architecture' },
      { icon: Boxes, label: 'Product planning' },
      { icon: Sparkles, label: 'Digital transformation strategy' },
    ]
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section data-testid="services-hero" className="relative py-24 lg:py-32 bg-gradient-to-b from-[#0A0C1C] to-[#12142B] overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#E65A14]/6 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              Our Services
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white">
              What We Build
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 text-base sm:text-lg text-[#C8C8C8] leading-relaxed max-w-2xl mx-auto">
              We specialise in building intelligent digital systems that simplify complexity and drive growth.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Blocks */}
      <section data-testid="services-detail" className="py-24 bg-[#0A0C1C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20">
          {serviceBlocks.map((block, i) => (
            <motion.div
              key={block.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              data-testid={`service-block-${i}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 !== 0 ? 'lg:direction-rtl' : ''}`}
            >
              <motion.div variants={fadeUp} custom={0} className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#E65A14]/20 to-[#FF8C28]/10 flex items-center justify-center mb-6">
                  <block.icon size={28} className="text-[#FF8C28]" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white mb-4">
                  {block.title}
                </h2>
                <p className="text-[#C8C8C8] leading-relaxed mb-8">{block.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {block.features.map(f => (
                    <div key={f.label} className="flex items-center gap-3 text-sm text-[#C8C8C8]">
                      <div className="w-8 h-8 rounded-md bg-[#FF8C28]/10 flex items-center justify-center flex-shrink-0">
                        <f.icon size={16} className="text-[#FF8C28]" />
                      </div>
                      {f.label}
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                variants={fadeUp}
                custom={1}
                className={`relative ${i % 2 !== 0 ? 'lg:order-1' : ''}`}
              >
                <div className="aspect-video rounded-xl bg-gradient-to-br from-[#12142B] to-[#0A0C1C] border border-[#FF8C28]/15 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#E65A14]/5 via-transparent to-[#FF8C28]/5" />
                  <block.icon size={80} className="text-[#FF8C28]/10" />
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-b from-[#0A0C1C] to-[#12142B]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} custom={0} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              Need a custom solution?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="mt-4 text-[#C8C8C8]">
              Let's discuss how we can build something tailored for your business.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="mt-8">
              <Link
                to="/contact"
                data-testid="services-cta-btn"
                className="inline-flex items-center px-10 py-4 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_30px_rgba(230,90,20,0.5)] transition-all duration-300"
              >
                Book a Free Consultation
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
